import { STORAGE_KEYS } from "@games-monorepo/shared-utils/storageKeys";

type WebkitMessageHandler = {
	postMessage: (payload: unknown) => unknown;
};

type WebkitMessageHandlers = Record<string, WebkitMessageHandler>;

export const APP_CONTEXT_FEATURE_FLAGS = {
	CAN_SEND_H2H_CHAT_CHALLENGE: "canSendH2HChatChallenge",
	CAN_SEED_GAMES_H2H_CHALLENGE: "canSeedGamesH2HChallenge",
	HAS_EARN_ARCADE: "hasEarnArcade",
	HAS_EARN_TASKS_ENABLED: "hasEarnTasksEnabled",
	HAS_PRISM_OFFERS: "hasPrismOffers",
	HAS_YAK_TRAILS_ONLINE: "hasYakTrailsOnline",
	HAS_YAK_TRAILS_PRACTICE: "hasYakTrailsPractice",
} as const;

export type AppContextFeatureFlags = {
	canSendH2HChatChallenge: boolean;
	canSeedGamesH2HChallenge: boolean;
	hasEarnArcade: boolean;
	hasEarnTasksEnabled: boolean;
	hasPrismOffers: boolean;
	hasYakTrailsOnline: boolean;
	hasYakTrailsPractice: boolean;
};

export type AppContextPayload = {
	authToken?: unknown;
	flags?: unknown;
	user?: {
		id?: unknown;
		roles?: unknown;
	} | null;
};

type HydrateAppContextOptions = {
	timeoutMs?: number;
	reason?: string;
};

const APP_CONTEXT_TIMEOUT_MS = 3000;
let pendingContextHydration: Promise<AppContextPayload | null> | null = null;
let hasLoggedMissingHandler = false;

const getAppContextHandler = (): WebkitMessageHandler | null => {
	if (typeof window === "undefined") return null;
	const webkit = (
		window as typeof window & {
			webkit?: { messageHandlers?: WebkitMessageHandlers };
		}
	).webkit;
	const handler = webkit?.messageHandlers?.getAppContext;
	if (!handler || typeof handler.postMessage !== "function") return null;
	return handler;
};

/**
 * Checks whether the native webview can return auth and user context.
 *
 * @returns True when the iOS getAppContext bridge is available.
 */
export const hasNativeAppContextBridge = (): boolean =>
	getAppContextHandler() !== null;

const asNonEmptyString = (value: unknown): string | null => {
	if (typeof value !== "string") return null;
	const trimmed = value.trim();
	return trimmed.length > 0 ? trimmed : null;
};

const asRecord = (value: unknown): Record<string, unknown> | null => {
	if (!value || typeof value !== "object" || Array.isArray(value)) return null;
	return value as Record<string, unknown>;
};

/**
 * Resolves feature capabilities bridged by a compatible native app.
 * Missing, malformed, and non-boolean values deliberately fail closed.
 */
export const getAppContextFeatureFlags = (
	context: AppContextPayload | null | undefined,
): AppContextFeatureFlags => {
	const flags = asRecord(context?.flags);
	return {
		canSendH2HChatChallenge:
			flags?.[APP_CONTEXT_FEATURE_FLAGS.CAN_SEND_H2H_CHAT_CHALLENGE] === true,
		canSeedGamesH2HChallenge:
			flags?.[APP_CONTEXT_FEATURE_FLAGS.CAN_SEED_GAMES_H2H_CHALLENGE] === true,
		hasEarnArcade:
			flags?.[APP_CONTEXT_FEATURE_FLAGS.HAS_EARN_ARCADE] === true,
		hasEarnTasksEnabled:
			flags?.[APP_CONTEXT_FEATURE_FLAGS.HAS_EARN_TASKS_ENABLED] === true,
		hasPrismOffers:
			flags?.[APP_CONTEXT_FEATURE_FLAGS.HAS_EARN_TASKS_ENABLED] === true &&
			flags?.[APP_CONTEXT_FEATURE_FLAGS.HAS_PRISM_OFFERS] === true,
		hasYakTrailsOnline:
			flags?.[APP_CONTEXT_FEATURE_FLAGS.HAS_YAK_TRAILS_ONLINE] === true,
		hasYakTrailsPractice:
			flags?.[APP_CONTEXT_FEATURE_FLAGS.HAS_YAK_TRAILS_PRACTICE] === true,
	};
};

/** Returns the non-empty role identifiers supplied by the native app. */
export const getAppContextUserRoles = (
	context: AppContextPayload | null | undefined,
): string[] => {
	if (!Array.isArray(context?.user?.roles)) return [];
	return context.user.roles
		.map(asNonEmptyString)
		.filter((role): role is string => role !== null);
};

const summarizeUserId = (value: string) => {
	if (value.length <= 8) return value;
	return `${value.slice(0, 8)}...`;
};

const withTimeout = async <T>(
	promise: Promise<T>,
	timeoutMs: number,
): Promise<T | null> => {
	let timeoutId: ReturnType<typeof setTimeout> | undefined;
	const timeoutPromise = new Promise<null>((resolve) => {
		timeoutId = setTimeout(() => resolve(null), timeoutMs);
	});

	try {
		return await Promise.race([promise, timeoutPromise]);
	} finally {
		if (timeoutId) {
			clearTimeout(timeoutId);
		}
	}
};

const persistAuthToken = (token: string) => {
	if (typeof window === "undefined") return;
	window.APP_AUTH_TOKEN = token;
	console.log("[ApiClient] Stored APP_AUTH_TOKEN from app context.", {
		tokenLength: token.length,
	});

	try {
		localStorage.setItem(STORAGE_KEYS.APP_AUTH_TOKEN, token);
		localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
	} catch (error) {
		console.warn("[ApiClient] Failed to persist auth token from app context.", error);
	}

	window.dispatchEvent(new Event("authTokenReady"));
};

const persistUserId = (userId: string) => {
	if (typeof window === "undefined") return;
	window.APP_USER_ID = userId;
	console.log("[ApiClient] Stored APP_USER_ID from app context.", {
		userId: summarizeUserId(userId),
	});

	try {
		localStorage.setItem(STORAGE_KEYS.APP_USER_ID, userId);
		localStorage.setItem(STORAGE_KEYS.USER_ID, userId);
	} catch (error) {
		console.warn("[ApiClient] Failed to persist user id from app context.", error);
	}
};

/**
 * Hydrate auth/user context from iOS native bridge when JS globals are missing.
 *
 * iOS can expose `window.webkit.messageHandlers.getAppContext.postMessage({})`
 * to synchronously or asynchronously return context with authToken and user.id.
 */
export const hydrateAppContextFromNativeBridge = async (
	options: HydrateAppContextOptions = {},
): Promise<AppContextPayload | null> => {
	const timeoutMs = options.timeoutMs ?? APP_CONTEXT_TIMEOUT_MS;
	const reason = options.reason ?? "unspecified";

	if (typeof window === "undefined") return null;
	const handler = getAppContextHandler();
	if (!handler) {
		if (!hasLoggedMissingHandler) {
			console.warn("[ApiClient] getAppContext handler not available on window.webkit.messageHandlers.", {
				reason,
			});
			hasLoggedMissingHandler = true;
		}
		return null;
	}
	hasLoggedMissingHandler = false;

	console.log("[ApiClient] Attempting getAppContext hydration.", { reason, timeoutMs });

	if (pendingContextHydration) {
		console.log("[ApiClient] Reusing in-flight getAppContext hydration.", { reason });
		return pendingContextHydration;
	}

	pendingContextHydration = (async () => {
		try {
			const rawResult = handler.postMessage({});
			console.log("[ApiClient] getAppContext postMessage dispatched.", {
				reason,
				returnType: typeof rawResult,
				isPromiseLike: Boolean(
					rawResult &&
						typeof rawResult === "object" &&
						"then" in rawResult,
				),
			});
			const rawContext = await withTimeout(Promise.resolve(rawResult), timeoutMs);

			if (!rawContext || typeof rawContext !== "object" || Array.isArray(rawContext)) {
				if (rawContext === null) {
					console.warn("[ApiClient] getAppContext timed out before returning data.", {
						reason,
						timeoutMs,
					});
				} else {
					console.warn("[ApiClient] getAppContext returned non-object payload.", {
						reason,
						payloadType: typeof rawContext,
					});
				}
				return null;
			}

			const context = rawContext as AppContextPayload;
			const authToken = asNonEmptyString(context.authToken);
			const userId = asNonEmptyString(context.user?.id);

			if (authToken) {
				persistAuthToken(authToken);
			}
			if (userId) {
				persistUserId(userId);
			}

			if (authToken || userId) {
				console.log("[ApiClient] Hydrated app context from native bridge.", {
					reason,
					hasAuthToken: Boolean(authToken),
					tokenLength: authToken?.length ?? 0,
					hasUserId: Boolean(userId),
					userId: userId ? summarizeUserId(userId) : null,
				});
			} else {
				console.warn("[ApiClient] getAppContext returned no authToken and no user.id.", {
					reason,
				});
			}

			return context;
		} catch (error) {
			console.warn("[ApiClient] Failed to get app context from native bridge.", error, {
				reason,
			});
			return null;
		} finally {
			pendingContextHydration = null;
		}
	})();

	return pendingContextHydration;
};
